import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { EnvelopeSimpleOpen, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export function OtpStep({ challenge, onBack, onSuccess }) {
  const { verifyOtp } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(30);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (value = code) => {
    if (value.length !== 6) return;
    setError(""); setLoading(true);
    try { await verifyOtp(challenge.challenge_id, value); onSuccess(); }
    catch (err) {
      const status = err.response?.status;
      setError(apiError(err.response?.data?.detail) || err.message); setCode("");
      if (status === 429 || status === 400) setTimeout(onBack, 1800);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-otp", { challenge_id: challenge.challenge_id }); toast.success("A new code is on its way"); setCooldown(30); setError(""); }
    catch (err) { setError(apiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div data-testid="otp-step">
      <div className="w-12 h-12 rounded-xl bg-blue-50 grid place-items-center mb-5"><EnvelopeSimpleOpen size={26} weight="duotone" className="text-blue-600" /></div>
      <h1 className="font-heading font-extrabold text-3xl text-slate-900">Check Your Email</h1>
      <p className="text-sm text-slate-500 mt-2 mb-7">We sent a 6-digit sign-in code to <b className="text-slate-800" data-testid="otp-email-hint">{challenge.email_hint}</b>. It expires in 10 minutes.</p>
      <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6) submit(v); }} autoFocus data-testid="otp-input" disabled={loading}>
        <InputOTPGroup className="gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg font-mono font-bold rounded-lg border border-slate-300 bg-white text-slate-900" />)}
        </InputOTPGroup>
      </InputOTP>
      {error && <p data-testid="otp-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      <Button onClick={() => submit()} disabled={loading || code.length !== 6} data-testid="otp-submit" className="w-full h-11 mt-5 bg-blue-600 text-white hover:bg-blue-700 font-semibold">
        <ShieldCheck size={18} weight="bold" className="mr-2" />{loading ? "Verifying…" : "Verify & Sign In"}
      </Button>
      <div className="mt-5 flex items-center justify-between text-sm">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-blue-600 transition-colors" data-testid="otp-back"><ArrowLeft size={14} />Back</button>
        <button onClick={resend} disabled={cooldown > 0} className="text-blue-600 font-semibold disabled:text-slate-400 transition-colors" data-testid="otp-resend">
          {cooldown > 0 ? `Resend Code in ${cooldown}s` : "Resend Code"}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-6">After verifying, you'll stay signed in on this device for 30 days.</p>
    </div>
  );
}
